/**
 * Service-worker updates for the installed PWA (ADR-0007). A plain
 * `location.reload()` does not activate a waiting service worker, so an
 * installed app below the minimum version stayed on "update required" for
 * ever. `applyUpdate()` activates the waiting worker (`updateSW(true)`, which
 * reloads once it controls the page); with no worker waiting it asks the
 * server for one first, and reloads as a last resort.
 */

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

let updateSW: UpdateSW | null = null;
let registration: ServiceWorkerRegistration | null = null;
let waiting = false;
let updateRequired = false;

/** Called from main.tsx once the service worker is registered. */
export function registerUpdater(fn: UpdateSW, reg?: ServiceWorkerRegistration | null): void {
  updateSW = fn;
  if (reg) registration = reg;
}

export function setRegistration(reg: ServiceWorkerRegistration | undefined | null): void {
  registration = reg ?? null;
}

/**
 * A new version is waiting. Applied at once when the app must update (the
 * gate is showing, nothing can be edited), or when no survey is open for
 * capture (we never reload under a reporter's hands otherwise).
 */
export function onNeedRefresh(path = typeof location === 'undefined' ? '' : location.pathname): void {
  waiting = true;
  if (updateRequired || !path.startsWith('/capture/')) void updateSW?.(true);
}

/** The version gate says this build is below the minimum (UpdateRequiredPage). */
export function markUpdateRequired(): void {
  updateRequired = true;
  if (waiting) void updateSW?.(true);
}

/** "Update now": activate the new service worker, whatever page is open. */
export async function applyUpdate(reload: () => void = () => window.location.reload()): Promise<void> {
  if (updateSW && waiting) {
    await updateSW(true);
    return;
  }
  if (registration) {
    try {
      await registration.update();
    } catch {
      /* offline: fall through */
    }
    const r = registration as ServiceWorkerRegistration & { waiting?: ServiceWorker | null };
    if (updateSW && r.waiting) {
      waiting = true;
      await updateSW(true);
      return;
    }
  }
  reload();
}

/**
 * A new version took control of this tab — applied here, from another tab, or
 * by the browser. Its cache no longer holds this page's older files, so the
 * page must reload or the next screen it opens fails to load. Not during
 * capture: the page there is already loaded, and any later screen that can't
 * load reloads by itself (app/PageErrorBoundary.tsx).
 */
export function watchForNewVersion(
  sw: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'>,
  reload: () => void = () => window.location.reload(),
  path: () => string = () => location.pathname,
): void {
  let hadController = !!sw.controller;
  sw.addEventListener('controllerchange', () => {
    // The first install takes control of a page that is already current.
    if (!hadController) {
      hadController = true;
      return;
    }
    if (!path().startsWith('/capture/')) reload();
  });
}

/** For tests. */
export function resetPwaState(): void {
  updateSW = null;
  registration = null;
  waiting = false;
  updateRequired = false;
}
